import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { VideoFeatures } from '@/store/videoFeatures/types';
import {
  ConversionJourney,
  defaultConversionJourney,
  FlowLayerExit,
  defaultFlowLayerExit,
  FlowLayerPlacement,
} from '@/store/videoFeatures/types/conversion-journey';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  PlaySquare,
  SkipForward,
  PlayCircle,
  MousePointerClick,
  Upload,
  TrendingUp,
  ArrowUpFromLine,
  ArrowDownToLine,
  Image as ImageIcon,
  X,
  Loader2,
  BarChart3,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { VideoLibraryModalWithFolders } from '@/components/video-editor/VideoLibraryModalWithFolders';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FlowLayerPlacementSelector } from './FlowLayerPlacementSelector';
import { ExitVideoBlock } from './ExitVideoBlock';
import { HeroCard } from '@/components/ui/HeroCard';
import { videoService } from '@/services/videoService';

function SkipAfterChips({
  value,
  customSec,
  onChange,
  onCustomChange,
}: {
  value: ConversionJourney['skipAfter'];
  customSec: number;
  onChange: (v: ConversionJourney['skipAfter']) => void;
  onCustomChange: (n: number) => void;
}) {
  useLanguage();
  const [open, setOpen] = React.useState(false);
  const presets: Array<{ id: ConversionJourney['skipAfter']; label: string }> = [
    { id: 10, label: '10 segundos' },
    { id: 30, label: '30 segundos' },
  ];

  const mins = Math.floor(customSec / 60);
  const secs = customSec % 60;
  const setMins = (m: number) => onCustomChange(Math.max(1, Math.min(3599, m * 60 + secs)));
  const setSecs = (s: number) => onCustomChange(Math.max(1, Math.min(3599, mins * 60 + s)));
  const fmt = (n: number) => String(Math.max(0, n)).padStart(2, '0');

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {presets.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={String(opt.id)}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] transition-colors',
              active
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            {opt.label}
          </button>
        );
      })}

      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o) onChange('custom');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] transition-colors flex items-center justify-center gap-1.5',
              value === 'custom'
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            <span>{translateInterfaceText("Personalizado")}</span>
            {value === 'custom' && (
              <span className="text-[11px] font-semibold text-[#4B7BFF]/80">
                · {mins > 0 ? `${mins}m ` : ''}
                {secs}s
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className="w-[260px] rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)]"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF]">
                {translateInterfaceText("Tempo personalizado")}</p>
              <span className="text-[11px] font-medium text-[#4B7BFF] tabular-nums">
                {fmt(mins)}:{fmt(secs)}
              </span>
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Minutos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={mins}
                  onChange={(e) => setMins(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <span className="pb-4 text-2xl font-light text-[#D1D5DB]">:</span>
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Segundos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={secs}
                  onChange={(e) => setSecs(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface Props {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

const AFTER_ACTION_OPTIONS: Array<{ id: ConversionJourney['afterAction']; label: string; desc: string }> = [
  {
    id: 'auto_start_main',
    get label() { return translateInterfaceText("Iniciar vídeo principal automaticamente"); },
    get desc() { return translateInterfaceText("O principal começa logo após o vídeo de entrada."); },
  },
  {
    id: 'wait_for_click',
    get label() { return translateInterfaceText("Aguardar clique do espectador"); },
    get desc() { return translateInterfaceText("Mostra um botão para iniciar o vídeo principal manualmente."); },
  },
];

const CTA_TIME_PRESETS: Array<{ id: number; label: string }> = [
  { id: 0, label: 'Desde o início' },
  { id: 5, label: 'Após 5 segundos' },
];

function CtaTimeChips({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  useLanguage();
  const [open, setOpen] = React.useState(false);
  const isCustom = !CTA_TIME_PRESETS.some((p) => p.id === value);
  const customVal = isCustom ? value : 10;
  const mins = Math.floor(customVal / 60);
  const secs = customVal % 60;
  const setMins = (m: number) => onChange(Math.max(1, Math.min(3599, m * 60 + secs)));
  const setSecs = (s: number) => onChange(Math.max(1, Math.min(3599, mins * 60 + s)));
  const fmt = (n: number) => String(Math.max(0, n)).padStart(2, '0');

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {CTA_TIME_PRESETS.map((opt) => {
        const active = !isCustom && value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] transition-colors',
              active
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            {opt.label}
          </button>
        );
      })}

      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o && !isCustom) onChange(10);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] whitespace-nowrap transition-colors',
              isCustom
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            {translateInterfaceText("Personalizado")}</button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className="w-[260px] rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)]"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF]">
                {translateInterfaceText("Tempo personalizado")}</p>
              <span className="text-[11px] font-medium text-[#4B7BFF] tabular-nums">
                {fmt(mins)}:{fmt(secs)}
              </span>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Minutos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={mins}
                  onChange={(e) => setMins(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <span className="pb-4 text-2xl font-light text-[#D1D5DB]">:</span>
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Segundos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={secs}
                  onChange={(e) => setSecs(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Section({
  icon,
  title,
  subtitle,
  children,
  rightSlot,
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  useLanguage();
  return (
    <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {icon && (
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{title}</h3>
            {subtitle && <p className="text-[12.5px] text-[#6B7280] leading-relaxed mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {rightSlot}
      </header>
      {children}
    </section>
  );
}

function RadioList<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ id: T; label: string; desc?: string }>;
  onChange: (v: T) => void;
}) {
  useLanguage();
  return (
    <div className="space-y-1.5">
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={String(opt.id)}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`w-full text-left flex items-start gap-3 px-3.5 py-2.5 rounded-[10px] border transition-colors ${
              active ? 'border-[#4B7BFF] bg-[#EEF2FF]' : 'border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]'
            }`}
          >
            <span
              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                active ? 'border-[#4B7BFF]' : 'border-[#CBD5E1]'
              }`}
            >
              {active && <span className="w-2 h-2 rounded-full bg-[#4B7BFF]" />}
            </span>
            <span className="min-w-0">
              <span className={`block text-[13px] ${active ? 'text-[#1A1A1A] font-medium' : 'text-[#374151]'}`}>
                {opt.label}
              </span>
              {opt.desc && <span className="block text-[11.5px] text-[#6B7280] mt-0.5">{opt.desc}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  highlight,
}: {
  title: string;
  value: string;
  subtitle: string;
  highlight?: boolean;
}) {
  useLanguage();
  return (
    <div
      className={`rounded-[14px] border p-4 bg-white ${
        highlight ? 'border-[#4B7BFF] ring-1 ring-[#4B7BFF]/15' : 'border-[#E5E7EB]'
      }`}
    >
      <p className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wide">{title}</p>
      <p
        className={`text-[18px] font-semibold tabular-nums mt-1 ${
          highlight ? 'text-[#4B7BFF]' : 'text-[#111827]'
        }`}
      >
        {value}
      </p>
      <p className="text-[10px] text-[#9CA3AF] mt-0.5">{subtitle}</p>
    </div>
  );
}

const pct = (num: number, den: number) =>
  den > 0 ? `${((num / den) * 100).toLocaleString(getInterfaceLanguage(), { maximumFractionDigits: 1 })}%` : '0%';
const fmtInt = (n: number) => Number(n || 0).toLocaleString(getInterfaceLanguage());

// Duração (segundos) -> "m:ss" para exibir no card do vídeo selecionado.
const formatDuration = (sec?: number) => {
  const s = Number(sec) || 0;
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60)
    .toString()
    .padStart(2, '0')}`;
};

function JourneyEvolutionChart({ title, data }: { title: string; data: any[] }) {
  useLanguage();
  return (
    <div className="rounded-[12px] border border-[#E5E7EB] bg-gradient-to-b from-white to-[#FAFBFF] p-4">
      <header className="mb-3 flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-[8px] bg-[#EEF2FF] flex items-center justify-center shrink-0 ring-1 ring-[#4B7BFF]/15">
          <TrendingUp className="w-3.5 h-3.5 text-[#4B7BFF]" strokeWidth={2.25} />
        </div>
        <div>
          <h3 className="text-[13px] font-semibold text-[#0F172A] tracking-tight">{title}</h3>
          <p className="text-[11.5px] text-[#6B7280] mt-0.5">{translateInterfaceText("Últimos 30 dias")}</p>
        </div>
      </header>
      <div className="h-56">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[12.5px] text-[#94A3B8]">
            {translateInterfaceText("Sem dados no período.")}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="#EEF2F7" vertical={false} strokeDasharray="3 4" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} dy={4} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={36} />
              <Tooltip
                contentStyle={{ fontSize: 14, borderRadius: 10, padding: '8px 12px' }}
                itemStyle={{ fontSize: 14, padding: 0 }}
                labelStyle={{ fontSize: 14, fontWeight: 600 }}
              />
              <Line type="monotone" dataKey="exibicoes" name="Exibições" stroke="#4B7BFF" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="pulos" name="Pulos" stroke="#CBD5E1" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="conclusoes" name="Conclusões" stroke="#1E3A8A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="chegada" name="Chegada ao principal" stroke="#60A5FA" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function JourneyMetrics({
  videoId,
  placement,
  exitEnabled,
}: {
  videoId?: string;
  placement: FlowLayerPlacement;
  exitEnabled: boolean;
}) {
  useLanguage();
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);

  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    setLoading(true);
    const fim = new Date();
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 30);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    videoService
      .getConversionJourneyMetrics(videoId, iso(inicio), iso(fim))
      .then((res: any) => {
        if (!cancelled) setMetrics(res?.data ?? res ?? null);
      })
      .catch(() => {
        if (!cancelled) setMetrics(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [videoId]);

  const showEntry = placement === 'before' || placement === 'before_and_after';
  const showExit = placement === 'after' || placement === 'before_and_after';
  const entry = metrics?.entry || { exibicoes: 0, plays: 0, pulos: 0, conclusoes: 0, ctaClicks: 0 };
  const exit = metrics?.exit || { exibicoes: 0, plays: 0, pulos: 0, conclusoes: 0, ctaClicks: 0 };
  const chegada = metrics?.chegaramAoPrincipal || 0;
  const timeline = metrics?.timeline || [];

  if (loading) {
    return (
      <div className="h-[200px] flex items-center justify-center text-[#94A3B8]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const hasData =
    entry.exibicoes > 0 ||
    entry.plays > 0 ||
    entry.ctaClicks > 0 ||
    exit.exibicoes > 0 ||
    exit.plays > 0 ||
    exit.ctaClicks > 0 ||
    chegada > 0;

  if (!hasData) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-[#94A3B8] mb-4" />
        <h3 className="text-[15px] font-medium text-[#1A1A1A] mb-2">{translateInterfaceText("Sem métricas ainda")}</h3>
        <p className="text-[13px] text-[#6B7280] max-w-md mx-auto">
          {translateInterfaceText("As métricas da Jornada aparecem aqui assim que os espectadores começarem a assistir aos vídeos de entrada e saída.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showEntry && (
        <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-5">
          <header className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-[10px] bg-[#EEF2FF] text-[#4B7BFF] flex items-center justify-center shrink-0">
              <ArrowUpFromLine className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <h4 className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Vídeo de entrada")}</h4>
              <p className="text-[11.5px] text-[#6B7280] leading-tight mt-0.5">
                {translateInterfaceText("Desempenho do vídeo exibido antes do principal.")}</p>
            </div>
          </header>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard title={translateInterfaceText("Exibições de entrada")} value={fmtInt(entry.exibicoes)} subtitle={translateInterfaceText("Vídeo de entrada exibido")} />
            <MetricCard title={translateInterfaceText("Conclusão da entrada")} value={pct(entry.conclusoes, entry.exibicoes)} subtitle={translateInterfaceText("Assistiram até o fim")} />
            <MetricCard title={translateInterfaceText("Pularam")} value={pct(entry.pulos, entry.exibicoes)} subtitle={translateInterfaceText("Clicaram em pular")} />
            <MetricCard title={translateInterfaceText("Chegaram ao principal")} value={pct(chegada, entry.exibicoes)} subtitle={translateInterfaceText("Avançaram ao conteúdo")} highlight />
            <MetricCard title={translateInterfaceText("Cliques no CTA de entrada")} value={fmtInt(entry.ctaClicks)} subtitle={translateInterfaceText("Clicaram no botão")} />
          </div>
          <JourneyEvolutionChart title={translateInterfaceText("Evolução da entrada")} data={timeline} />
        </div>
      )}

      {showExit && (
        <div className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-5">
          <header className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-[10px] bg-[#EEF2FF] text-[#4B7BFF] flex items-center justify-center shrink-0">
              <ArrowDownToLine className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <h4 className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Vídeo de saída")}</h4>
              <p className="text-[11.5px] text-[#6B7280] leading-tight mt-0.5">
                {translateInterfaceText("Desempenho do vídeo exibido depois do principal.")}</p>
            </div>
          </header>
          {exitEnabled ? (
            <div className="grid grid-cols-2 gap-3">
              <MetricCard title={translateInterfaceText("Exibições de saída")} value={fmtInt(exit.exibicoes)} subtitle={translateInterfaceText("Vídeo final exibido")} />
              <MetricCard title={translateInterfaceText("Conclusão da saída")} value={pct(exit.conclusoes, exit.exibicoes)} subtitle={translateInterfaceText("Assistiram até o fim")} />
              <MetricCard title={translateInterfaceText("Cliques no CTA final")} value={fmtInt(exit.ctaClicks)} subtitle={translateInterfaceText("Ações geradas")} highlight />
              <MetricCard title={translateInterfaceText("CTR do CTA final")} value={pct(exit.ctaClicks, exit.exibicoes)} subtitle={translateInterfaceText("Cliques sobre exibições")} />
            </div>
          ) : (
            <div className="rounded-[12px] border border-dashed border-[#4B7BFF]/30 bg-[#F5F8FF] p-4 text-center">
              <p className="text-[12.5px] text-[#374151] font-medium">{translateInterfaceText("Vídeo de saída não configurado")}</p>
              <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                {translateInterfaceText("Ative o bloco de saída para visualizar suas métricas aqui.")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Card de CTA estilo YouTube (preview da entrada).
function YouTubeCtaCard({
  title,
  description,
  ctaLabel,
  url,
  image,
}: {
  title: string;
  description?: string;
  ctaLabel: string;
  url?: string;
  image?: string;
}) {
  useLanguage();
  let host = '';
  try {
    if (url) host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch {}
  const favicon = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : '';
  return (
    <div
      className="absolute bottom-[12px] left-[10px] flex items-center gap-2 rounded-[10px] bg-black/55 backdrop-blur-md p-1.5 pr-2 ring-1 ring-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.35)] transition-all overflow-hidden"
      style={{ maxWidth: 'calc(100% - 105px)', minHeight: '34px' }}
    >
      <div className="w-6 h-6 rounded-[7px] bg-[#F1F3F5] shrink-0 overflow-hidden flex items-center justify-center ring-1 ring-black/5">
        {image ? (
          <img src={image} alt="" className="w-full h-full object-cover" />
        ) : favicon ? (
          <img src={favicon} alt="" className="w-4 h-4 object-contain" />
        ) : (
          <div className="w-4 h-4 rounded-sm bg-gradient-to-br from-slate-300 to-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-white leading-[1.1] truncate" style={{ maxWidth: '130px' }}>
          {title}
        </p>
        {description ? (
          <p className="text-[9px] text-white/70 leading-[1.1] truncate" style={{ maxWidth: '130px' }}>
            {description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        className="shrink-0 rounded-[8px] bg-white/90 hover:bg-white text-[#0F0F0F] text-[10px] font-semibold px-2.5 h-[26px] whitespace-nowrap transition-colors"
      >
        {ctaLabel}
      </button>
    </div>
  );
}

function JourneyPreview({ pv }: { pv: ConversionJourney }) {
  useLanguage();
  const skipSeconds = pv.skipAfter === 'custom' ? pv.skipAfterCustomSec : pv.skipAfter;
  return (
    <div className="rounded-[14px] border border-[#E5E7EB] bg-[#F9FAFB] p-3">
      <p className="text-[11.5px] text-[#6B7280] mb-2">{translateInterfaceText("Vídeo de entrada")}</p>
      <div
        className="relative aspect-video rounded-[10px] overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}
      >
        {pv.libraryVideoThumbnail && (
          <img
            src={pv.libraryVideoThumbnail}
            alt={pv.libraryVideoName || translateInterfaceText("Vídeo de entrada")}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {pv.libraryVideoThumbnail && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        )}

        {pv.skipEnabled && (
          <div
            className="absolute right-[10px] flex items-center gap-1.5 pl-2.5 pr-2 h-[26px] text-white text-[10px] font-medium rounded-full"
            style={{ bottom: '14px', background: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(6px)' }}
          >
            <span>{translateInterfaceText("Pular em")}{" "}{skipSeconds}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 4l12 8-12 8V4z" />
            </svg>
          </div>
        )}

        {pv.ctaEnabled && (
          <YouTubeCtaCard
            title={pv.ctaTitle || translateInterfaceText("Saiba mais")}
            description={pv.ctaDescription}
            ctaLabel={pv.ctaText || 'Saiba mais'}
            url={pv.ctaUrl}
            image={pv.ctaImage}
          />
        )}
      </div>
    </div>
  );
}

export const ConversionJourneyConfig: React.FC<Props> = ({ features, setFeature }) => {
  useLanguage();
  const videoId = features.videoId;
  const pv: ConversionJourney = (features.conversionJourney as ConversionJourney) ?? defaultConversionJourney;
  const placement: FlowLayerPlacement = pv.placementMode ?? 'before';
  const exit: FlowLayerExit = pv.exit ?? defaultFlowLayerExit;
  const showEntry = placement === 'before' || placement === 'before_and_after';
  const showExit = placement === 'after' || placement === 'before_and_after';

  const update = (patch: Partial<ConversionJourney>) =>
    setFeature('conversionJourney', { ...pv, ...patch });
  const updateExit = (next: FlowLayerExit) => update({ exit: next });
  const [activeTab, setActiveTab] = useState<'basic' | 'metrics'>('basic');
  const [pickerOpen, setPickerOpen] = useState(false);
  const disabled = !pv.enabled;

  // Upload binário da imagem do CTA da entrada (Bunny) — nunca base64.
  const handleEntryCtaImage = async (file: File) => {
    if (videoId) {
      try {
        const { fileUrl } = await videoService.uploadConversionJourneyImage(videoId, 'entry-cta', file);
        update({ ctaImage: fileUrl });
        return;
      } catch (e) {
        // fallback base64 (BE faz upload no save)
      }
    }
    const reader = new FileReader();
    reader.onload = () => update({ ctaImage: String(reader.result) });
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-4 space-y-4 overflow-x-hidden">
      <HeroCard
        icon={<PlaySquare className="w-full h-full" strokeWidth={2} />}
        title={translateInterfaceText("Jornada de Conversão")}
        description={translateInterfaceText("Adicione vídeos antes e depois do conteúdo principal para aquecer, conduzir e converter espectadores.")}
        isActive={pv.enabled}
        onToggle={(v) => update({ enabled: v })}
      />

      {pv.enabled && (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'basic' | 'metrics')} className="w-full">
          <TabsList className="w-full bg-[#F7F9FC] rounded-xl p-1">
            <TabsTrigger
              value="basic"
              className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
            >
              {translateInterfaceText("Personalização")}</TabsTrigger>
            <TabsTrigger
              value="metrics"
              className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
            >
              {translateInterfaceText("Métricas")}</TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="mt-4">
            <JourneyMetrics videoId={videoId} placement={placement} exitEnabled={!!exit.enabled} />
          </TabsContent>

          <TabsContent value="basic" className="mt-4">
            {/* min-w-0 + reset: fieldset tem min-inline-size:min-content por
                padrão, que estoura a largura e desalinha em relação ao tab de
                Métricas (que usa div). Neutralizado para igualar as caixas. */}
            <fieldset disabled={disabled} className="space-y-4 min-w-0 w-full m-0 p-0 border-0">
              <FlowLayerPlacementSelector value={placement} onChange={(v) => update({ placementMode: v })} />

              {showEntry && (
                <>
                  <JourneyPreview pv={pv} />

                  <Section
                    icon={<ArrowUpFromLine className="w-5 h-5 text-[#4B7BFF]" />}
                    title={translateInterfaceText("Vídeo de entrada")}
                    subtitle={translateInterfaceText("Exibido antes do principal para preparar o espectador.")}
                  >
                    <div className="rounded-[12px] border border-[#E5E7EB] bg-[#FAFBFC] p-3 flex items-center gap-3">
                      <div className="w-14 h-10 rounded-[8px] bg-[#0F172A] flex items-center justify-center shrink-0 overflow-hidden">
                        {pv.libraryVideoThumbnail ? (
                          <img src={pv.libraryVideoThumbnail} alt={pv.libraryVideoName || ''} className="w-full h-full object-cover" />
                        ) : (
                          <PlayCircle className="w-5 h-5 text-white/80" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-[#1A1A1A] truncate">
                          {pv.libraryVideoName || translateInterfaceText("Selecione um vídeo da biblioteca")}
                        </p>
                        <p className="text-[11.5px] text-[#6B7280]">
                          {pv.libraryVideoDuration ? translateInterfaceText("Duração: {value0}", { value0: (pv.libraryVideoDuration) }) : translateInterfaceText("Nenhum vídeo selecionado")}
                        </p>
                      </div>
                      {pv.libraryVideoId && (
                        <button
                          type="button"
                          className="h-9 px-3 rounded-[8px] text-[12.5px] text-[#6B7280] hover:text-[#1A1A1A] hover:bg-white"
                          onClick={() =>
                            update({
                              libraryVideoId: null,
                              libraryVideoName: '',
                              libraryVideoDuration: '',
                              libraryVideoThumbnail: '',
                            })
                          }
                        >
                          {translateInterfaceText("Remover")}</button>
                      )}
                      <button
                        type="button"
                        className="h-9 px-3 rounded-[8px] border border-[#E5E7EB] text-[12.5px] text-[#374151] hover:bg-white"
                        onClick={() => setPickerOpen(true)}
                      >
                        {pv.libraryVideoId ? translateInterfaceText("Trocar") : translateInterfaceText("Selecionar")}
                      </button>
                    </div>
                  </Section>

                  <Section
                    icon={<SkipForward className="w-5 h-5 text-[#4B7BFF]" />}
                    title={translateInterfaceText("Botão de pular")}
                    subtitle={translateInterfaceText("Permite pular o vídeo de entrada.")}
                    rightSlot={<Switch checked={pv.skipEnabled} onCheckedChange={(v) => update({ skipEnabled: v })} />}
                  >
                    {pv.skipEnabled && (
                      <div className="space-y-3">
                        <div>
                          <p className="text-[12px] text-[#374151] mb-2">{translateInterfaceText("Liberar botão após")}</p>
                          <SkipAfterChips
                            value={pv.skipAfter}
                            customSec={pv.skipAfterCustomSec}
                            onChange={(v) => update({ skipAfter: v })}
                            onCustomChange={(n) => update({ skipAfter: 'custom', skipAfterCustomSec: n })}
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto do botão")}</Label>
                            <Input value={pv.skipButtonText} onChange={(e) => update({ skipButtonText: e.target.value })} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto antes de liberar")}</Label>
                            <Input
                              value={pv.skipCountdownText}
                              onChange={(e) => update({ skipCountdownText: e.target.value })}
                              placeholder={translateInterfaceText("Pular em {segundos}s")}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Section>

                  <Section
                    icon={<PlayCircle className="w-5 h-5 text-[#4B7BFF]" />}
                    title={translateInterfaceText("Após o vídeo de entrada")}
                    subtitle={translateInterfaceText("O que acontece ao terminar ou pular.")}
                  >
                    <RadioList value={pv.afterAction} options={AFTER_ACTION_OPTIONS} onChange={(v) => update({ afterAction: v })} />
                    {pv.afterAction === 'wait_for_click' && (
                      <div className="mt-3">
                        <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto do botão para iniciar")}</Label>
                        <Input value={pv.startButtonText} onChange={(e) => update({ startButtonText: e.target.value })} className="mt-1" />
                      </div>
                    )}
                  </Section>

                  <Section
                    icon={<MousePointerClick className="w-5 h-5 text-[#4B7BFF]" />}
                    title={translateInterfaceText("CTA do vídeo de entrada")}
                    subtitle={translateInterfaceText("Exiba um botão de ação durante o vídeo de entrada.")}
                    rightSlot={<Switch checked={pv.ctaEnabled} onCheckedChange={(v) => update({ ctaEnabled: v })} />}
                  >
                    {pv.ctaEnabled && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Imagem (favicon/logo)")}</Label>
                          <div className="mt-1.5 flex items-center gap-3">
                            <div className="relative group">
                              <label className="cursor-pointer block">
                                <div
                                  className={`w-14 h-14 rounded-[12px] overflow-hidden flex items-center justify-center transition-all ${
                                    pv.ctaImage
                                      ? 'ring-1 ring-black/5 bg-white'
                                      : 'border border-dashed border-[#D1D5DB] bg-gradient-to-br from-[#F8FAFC] to-[#EEF2F7] hover:border-[#4B7BFF] hover:from-[#F0F5FF] hover:to-[#E8EFFF]'
                                  }`}
                                >
                                  {pv.ctaImage ? (
                                    <>
                                      <img src={pv.ctaImage} alt="" className="w-full h-full object-cover" />
                                      <div className="absolute inset-0 rounded-[12px] bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <Upload className="w-4 h-4 text-white" strokeWidth={2} />
                                      </div>
                                    </>
                                  ) : (
                                    <ImageIcon className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.5} />
                                  )}
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleEntryCtaImage(file);
                                  }}
                                />
                              </label>
                              {pv.ctaImage && (
                                <button
                                  type="button"
                                  onClick={() => update({ ctaImage: '' })}
                                  aria-label={translateInterfaceText("Remover imagem")}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white ring-1 ring-black/10 shadow-sm flex items-center justify-center text-[#6B7280] hover:text-[#111827] hover:ring-black/20 transition-all"
                                >
                                  <X className="w-3 h-3" strokeWidth={2.5} />
                                </button>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[12px] font-medium text-[#374151]">
                                {pv.ctaImage ? translateInterfaceText("Imagem carregada") : translateInterfaceText("Adicionar imagem")}
                              </span>
                              <span className="text-[11px] text-[#9CA3AF]">{translateInterfaceText("PNG, JPG ou SVG · até 2 MB")}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Título")}</Label>
                          <Input
                            value={pv.ctaTitle}
                            onChange={(e) => update({ ctaTitle: e.target.value })}
                            placeholder={translateInterfaceText("Ex: Diagnóstico Comercial Gratuito")}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Descrição (opcional)")}</Label>
                          <Input
                            value={pv.ctaDescription}
                            onChange={(e) => update({ ctaDescription: e.target.value })}
                            placeholder={translateInterfaceText("Ex: Agende agora sem custos")}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto do botão")}</Label>
                            <Input value={pv.ctaText} onChange={(e) => update({ ctaText: e.target.value })} placeholder={translateInterfaceText("Saiba mais")} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-[12px] text-[#374151]">{translateInterfaceText("URL de destino")}</Label>
                            <Input
                              value={pv.ctaUrl}
                              onChange={(e) => update({ ctaUrl: e.target.value })}
                              placeholder="https://seudominio.com/oferta"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-[12px] text-[#374151] mb-2">{translateInterfaceText("Quando exibir o CTA")}</p>
                          <CtaTimeChips value={pv.ctaShowAfterSec} onChange={(v) => update({ ctaShowAfterSec: v })} />
                        </div>
                      </div>
                    )}
                  </Section>
                </>
              )}

              {showExit && <ExitVideoBlock exit={exit} onChange={updateExit} videoId={videoId} />}
            </fieldset>
          </TabsContent>
        </Tabs>
      )}

      <VideoLibraryModalWithFolders
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={translateInterfaceText("Selecionar vídeo de entrada")}
        onSelect={(v) =>
          update({
            sourceType: 'library',
            libraryVideoId: v.id,
            libraryVideoName: v.title,
            libraryVideoDuration: formatDuration(v.duration),
            libraryVideoThumbnail: v.thumbnailUrl ?? '',
          })
        }
      />
    </div>
  );
};

export default ConversionJourneyConfig;
